import classNames from "classnames"
import {kebabCase, startCase} from "lodash"
import moment from "moment"
import React, {ReactElement} from "react"
import {useNavigate} from "react-router-dom"
import ReactTooltip from "react-tooltip"
import {useModal} from "react-modal-hook"
import {CheckPayment, CheckPaymentsStatus} from "../../resources/checkPayments"
import {Resource} from "../../resources/common"
import {DataTableCell, DataTableRow} from "../DataTable/DataTable"
import {OrgName} from "../OrgName/OrgName"
import {AllowedCheckPaymentsColumns, CheckPaymentsColumns} from "./CheckPayments"
import numeral from "numeral"
import {Customer, getCustomerName} from "../../resources/customer"
import {ActionButton, ActionItem} from "../ActionButton/ActionButton"
import {TitleBarAction} from "../TitleBar/TitleBar"
import {CancelCheckPaymentModal} from "./CancelCheckPaymentModal"
import {StopCheckPaymentModal} from "./StopCheckPaymentModal"
import {CheckStopPayment} from "../../resources/stopPayments"
import IdLink from "../IdLink/IdLink"
import {StopPaymentIcon} from "./StopPaymentIcon"

interface CheckPaymentRowProps {
	checkPayment: CheckPayment
	include?: Resource[]
	showOrgColumn?: boolean
	includedColumns: AllowedCheckPaymentsColumns[]
	refresh: () => void
	selectedCheckPaymentId?: string
	onClick?: (e: React.MouseEvent<HTMLTableRowElement>) => void
	enableActions?: boolean
	activeStopPayments?: CheckStopPayment[]
}

export function CheckPaymentRow({
	checkPayment,
	include,
	includedColumns,
	selectedCheckPaymentId,
	onClick,
	enableActions = false,
	refresh,
	activeStopPayments = [],
}: CheckPaymentRowProps) {
	const navigate = useNavigate()
	const id = checkPayment.id
	const org = checkPayment.relationships.org?.data.id
	const customerId = checkPayment.relationships.customer?.data.id
	const customersIds = checkPayment.relationships.customers?.data
	const accountId = checkPayment.relationships.account.data.id
	const status = checkPayment.attributes.status
	const returnReason = checkPayment.attributes.returnStatusReason
	const rejectReason = checkPayment.attributes.rejectReason
	const additionalVerification = checkPayment.attributes.additionalVerificationStatus
	const createdAt = checkPayment.attributes.createdAt
	const amount = checkPayment.attributes.amount
	const sendAt = checkPayment.attributes?.sendAt
	const checkNumber = checkPayment.attributes.checkNumber
	const isAccountClosureCheck = checkPayment.attributes.isAccountClosureCheck

	const customer =
		customerId && include
			? include.find(
					(r) => (r.type == "individualCustomer" || r.type == "businessCustomer") && r.id == customerId.toString()
			  )
			: undefined

	const customers =
		customersIds && include
			? (include.filter((i) => ["individualCustomer", "businessCustomer"].includes(i.type)) as Customer[])
			: undefined

	let customerName = ""

	if (customer) {
		if (customer.type == "individualCustomer") {
			customerName = `${customer.attributes.fullName.first} ${customer.attributes.fullName.last}`
		} else if (customer.type == "businessCustomer") {
			customerName = customer.attributes.name
		}
	}

	if (customers) {
		customers.forEach((customer) => {
			const name = getCustomerName(customer)

			if (customerName === "") {
				customerName += name
			} else {
				customerName += ", " + name
			}
		})
	}

	const contentColumns: Partial<Record<AllowedCheckPaymentsColumns, ReactElement>> = {}

	if (includedColumns.includes(CheckPaymentsColumns.id)) {
		contentColumns["Id"] = (
			<DataTableCell
				className={classNames(id == selectedCheckPaymentId && "selected-data-table-id", "data-table-id-cell")}
			>
				<IdLink className="data-table-id" href={`/check-payments/${id}`}>
					{id}
				</IdLink>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(CheckPaymentsColumns.org) && org) {
		contentColumns["Org"] = <OrgName orgId={org.toString()} included={include} />
	}

	if (includedColumns.includes(CheckPaymentsColumns.customer)) {
		contentColumns["Customer"] = (
			<DataTableCell>
				<a
					className="link"
					onClick={(e) => {
						e.preventDefault()
						e.stopPropagation()
						if (customer) {
							navigate(`/${customer.type == "individualCustomer" ? "individual" : "business"}/${customerId}`)
						}
					}}
				>
					{customerName}
				</a>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(CheckPaymentsColumns.accountId)) {
		contentColumns["Account"] = (
			<DataTableCell>
				<a
					className="link"
					onClick={(e) => {
						e.preventDefault()
						e.stopPropagation()
						if (accountId) {
							navigate(`/accounts/${accountId}`)
						}
					}}
				>
					{accountId}
				</a>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(CheckPaymentsColumns.amount)) {
		contentColumns["Amount"] = (
			<DataTableCell>
				<span className="payments-amount">{numeral(amount / 100).format("$0,0.00")}</span>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(CheckPaymentsColumns.status)) {
		let tooltipValue: string | undefined = undefined
		if (returnReason) {
			tooltipValue = startCase(returnReason)
		}

		if (rejectReason) {
			tooltipValue = startCase(rejectReason)
		}

		contentColumns["Status"] = (
			<DataTableCell>
				<div
					data-tip="React-tooltip"
					data-for={`status-reason-tooltip-${id}`}
					className={classNames("check-payments-status", kebabCase(status))}
				>
					{startCase(status)}

					<ReactTooltip
						className="tooltip-info"
						id={`status-reason-tooltip-${id}`}
						disable={!tooltipValue}
						effect="solid"
					>
						{tooltipValue}
					</ReactTooltip>
				</div>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(CheckPaymentsColumns.createdAt)) {
		contentColumns["Created At"] = <DataTableCell>{moment(createdAt).format("L LT")}</DataTableCell>
	}

	if (includedColumns.includes(CheckPaymentsColumns.additionalVerification)) {
		contentColumns["Additional Verification"] = <DataTableCell>{additionalVerification}</DataTableCell>
	}

	if (includedColumns.includes(CheckPaymentsColumns.sendAt)) {
		contentColumns["Send At"] = <DataTableCell>{sendAt ? moment(sendAt).format("L LT") : ""}</DataTableCell>
	}

	if (includedColumns.includes(CheckPaymentsColumns.checkNumber)) {
		const activeStopPayment = activeStopPayments.find(
			(stopPayment) => stopPayment.attributes.checkNumber === checkNumber
		)
		const stopPaymentTooltipId = `stop-payment-tooltip-${checkPayment.id}`

		contentColumns["Check Number"] = (
			<DataTableCell>
				<div style={{display: "flex", alignItems: "center", gap: "8px"}}>
					<span>{checkNumber}</span>
					{activeStopPayment && (
						<StopPaymentIcon tooltipId={stopPaymentTooltipId} createdAt={activeStopPayment.attributes.createdAt} />
					)}
				</div>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(CheckPaymentsColumns.isAccountClosureCheck)) {
		contentColumns["Purpose"] = (
			<DataTableCell>{isAccountClosureCheck ? "Account Closure" : "General Use"}</DataTableCell>
		)
	}

	if (includedColumns.includes(CheckPaymentsColumns.actions) && enableActions) {
		const canCancel = status === CheckPaymentsStatus.Pending
		const canStop =
			status === CheckPaymentsStatus.New ||
			status === CheckPaymentsStatus.InDelivery ||
			status === CheckPaymentsStatus.Delivered ||
			status === CheckPaymentsStatus.InProduction ||
			status === CheckPaymentsStatus.ReturnedToSender ||
			status === CheckPaymentsStatus.MarkedForReturn ||
			status === CheckPaymentsStatus.Returned

		// Check if this check has an active stop payment
		const activeStopPayment = activeStopPayments.find(
			(stopPayment) => stopPayment.attributes.checkNumber === checkNumber
		)

		const [showCancelModal, hideCancelModal] = useModal(
			() => <CancelCheckPaymentModal checkPayment={checkPayment} closeModal={hideCancelModal} onSuccess={refresh} />,
			[checkPayment.id]
		)

		const [showStopModal, hideStopModal] = useModal(
			() => (
				<StopCheckPaymentModal
					checkPayment={checkPayment}
					closeModal={hideStopModal}
					onSuccess={refresh}
					mode={activeStopPayment ? "remove" : "create"}
					existingStopPayment={activeStopPayment}
				/>
			),
			[checkPayment.id, activeStopPayment]
		)

		contentColumns["Actions"] = (
			<DataTableCell>
				{canCancel || canStop || activeStopPayment ? (
					<TitleBarAction>
						<ActionButton enableActions={true}>
							{canCancel && (
								<ActionItem
									title="Cancel Check Payment"
									icon="delete-close-x"
									onClick={showCancelModal}
									color={"#FF506F"}
								/>
							)}
							{activeStopPayment ? (
								<ActionItem title="Remove Stop Payment" icon="cancel-stop-payment" onClick={showStopModal} />
							) : (
								canStop && (
									<ActionItem
										title="Stop Check Payment"
										icon="stop-payment"
										onClick={showStopModal}
										color={"#FF506F"}
									/>
								)
							)}
						</ActionButton>
					</TitleBarAction>
				) : null}
			</DataTableCell>
		)
	}

	const content: Array<ReactElement | null> = []

	includedColumns.forEach((col, i) => {
		if (col in contentColumns && contentColumns[col]) {
			const column = {...contentColumns[col]} as ReactElement
			column.key = i.toString()
			content.push(column)
		}
	})

	return (
		<DataTableRow
			className={classNames(id === selectedCheckPaymentId && "selected-data-table-row")}
			onClick={(e) => onClick && onClick(e)}
		>
			{content}
		</DataTableRow>
	)
}
